import { useQuery } from "@tanstack/react-query";
import { BookOpen, LayoutDashboard, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CommandGroup, CommandItem, CommandList } from "@nexus/ui";
import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import { useAppContext } from "@/contexts/AppContext";
import { useAccount } from "@/hooks/useAccount";
import { buildAnalyticsTimeRange } from "@/lib/dashboard/timeRange";
import { useRestClient } from "@/lib/restClient";
import { getAuthorizedNavGroups, getNavGroupEntryHref, NAV_GROUPS } from "./NavSidebar";

export interface GlobalSearchResult {
  kind: "dashboard" | "journal";
  id: string;
  title: string;
  subtitle: string;
  slug?: string;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
}

export interface SearchPlace {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
}

const RECENT_LIMIT = 4;

/** Results below the persistent search field; the input stays in the top bar. */
export function GlobalSearchResults({ value, onClose }: { value: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const apiFetch = useRestClient();
  const authorization = useOptionalAuthorization();
  const canSearchJournal = authorization?.canAccessSection("journal") ?? false;
  const accountId = useAccount().data?.account_id;
  const { analyticsTimeRange, selectedTenantIds } = useAppContext();
  const [debouncedValue, setDebouncedValue] = useState("");
  const [recentIds, setRecentIds] = useState<string[]>([]);

  const places = useMemo(
    () =>
      authorization && accountId
        ? getAuthorizedNavGroups(NAV_GROUPS, authorization.canAccessSection)
        : [],
    [accountId, authorization],
  );

  // Remember the place the search was opened from, most recent first.
  useEffect(() => {
    if (!accountId || !places.length) return;
    const key = `nexus.search.places.${accountId}`;
    let stored: string[] = [];
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
      if (Array.isArray(parsed))
        stored = parsed.filter((id): id is string => typeof id === "string");
    } catch {
      // Storage may be unavailable; the places list still works.
    }
    const current = places.find(
      (place) =>
        pathname === place.basePath ||
        pathname.startsWith(`${place.basePath}/`) ||
        (place.id === "live" && pathname.startsWith("/journal")),
    );
    const ids = current
      ? [current.id, ...stored.filter((id) => id !== current.id)].slice(0, RECENT_LIMIT)
      : stored.slice(0, RECENT_LIMIT);
    setRecentIds(ids);
    try {
      localStorage.setItem(key, JSON.stringify(ids));
    } catch {
      /* private browsing */
    }
  }, [accountId, pathname, places]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [value]);

  const trimmed = value.trim();
  const ready = trimmed.length >= 2 && trimmed === debouncedValue;
  const timeRange = useMemo(
    () => buildAnalyticsTimeRange(analyticsTimeRange),
    [analyticsTimeRange],
  );
  const tenants = canSearchJournal ? selectedTenantIds.join(",") : "";
  const query = useQuery({
    queryKey: [
      "global-search",
      accountId,
      authorization?.profile,
      debouncedValue,
      timeRange,
      tenants,
    ],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        q: debouncedValue,
        from_ts: timeRange.from,
        to_ts: timeRange.to,
      });
      if (tenants) params.set("tenant_ids", tenants);
      return apiFetch<GlobalSearchResponse>(`/api/search?${params}`, { signal });
    },
    enabled: ready && Boolean(accountId),
    staleTime: 30_000,
    retry: false,
  });

  const results = ready ? (query.data?.results ?? []) : [];
  const isPending = trimmed.length >= 2 && (!ready || query.isPending);
  const isError = ready && query.isError;

  const destinations: SearchPlace[] = places.map((place) => ({
    id: place.id,
    label: place.label,
    href: getNavGroupEntryHref(place),
    icon: place.icon,
  }));
  const recent = recentIds.flatMap((id) => destinations.filter((place) => place.id === id));
  const term = trimmed.toLocaleLowerCase();
  const matchingPlaces = term
    ? destinations.filter((place) => place.label.toLocaleLowerCase().includes(term))
    : destinations.filter((place) => !recent.some((item) => item.id === place.id));
  const dashboards = results.filter((item) => item.kind === "dashboard");
  const journal = canSearchJournal ? results.filter((item) => item.kind === "journal") : [];
  const showResults = term.length >= 2 && !isPending && !isError;

  function go(href: string) {
    navigate(href);
    onClose();
  }

  function selectResult(result: GlobalSearchResult) {
    if (result.kind === "dashboard" && result.slug) {
      go(`/workspace/dashboards/${encodeURIComponent(result.slug)}`);
    } else if (result.kind === "journal") {
      go(`/journal?search=${encodeURIComponent(trimmed)}`);
    }
  }

  return (
    <>
      <CommandList className="max-h-[min(65vh,28rem)] p-2">
        {!term && recent.length > 0 ? (
          <PlaceGroup heading="Recently visited" items={recent} onSelect={go} />
        ) : null}
        {matchingPlaces.length > 0 ? (
          <PlaceGroup heading="Places" items={matchingPlaces} onSelect={go} />
        ) : null}
        {isPending ? (
          <p role="status" className="px-3 py-6 text-center text-sm text-muted">
            Searching…
          </p>
        ) : null}
        {isError ? (
          <p role="alert" className="px-3 py-6 text-center text-sm text-muted">
            Search is unavailable. Try again.
          </p>
        ) : null}
        {showResults && dashboards.length > 0 ? (
          <ResultGroup heading="Dashboards" items={dashboards} onSelect={selectResult} />
        ) : null}
        {showResults && journal.length > 0 ? (
          <ResultGroup
            heading="Journal · current time range"
            items={journal}
            onSelect={selectResult}
          />
        ) : null}
        {showResults && dashboards.length + journal.length === 0 && matchingPlaces.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted">No results found.</p>
        ) : null}
      </CommandList>

      <div className="hidden items-center gap-4 border-t border-border px-4 py-2 text-2xs text-muted sm:flex">
        <span>↑↓ to navigate</span>
        <span>↵ to open</span>
        <span>Esc to close</span>
      </div>
    </>
  );
}

function PlaceGroup({
  heading,
  items,
  onSelect,
}: {
  heading: string;
  items: SearchPlace[];
  onSelect: (href: string) => void;
}) {
  return (
    <CommandGroup heading={heading}>
      {items.map(({ id, label, href, icon: Icon }) => (
        <CommandItem
          key={`${heading}-${id}`}
          value={`${heading}-${id}`}
          onSelect={() => onSelect(href)}
          className="cursor-pointer gap-3 px-3 font-medium"
        >
          <Icon className="h-4 w-4 shrink-0 text-muted" aria-hidden />
          <span className="truncate">{label}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function ResultGroup({
  heading,
  items,
  onSelect,
}: {
  heading: string;
  items: GlobalSearchResult[];
  onSelect: (result: GlobalSearchResult) => void;
}) {
  return (
    <CommandGroup heading={heading}>
      {items.map((item, index) => {
        const Icon = item.kind === "dashboard" ? LayoutDashboard : BookOpen;
        return (
          <CommandItem
            key={`${item.kind}-${item.id}-${index}`}
            value={`${item.kind}-${item.id}-${index}`}
            onSelect={() => onSelect(item)}
            className="cursor-pointer gap-3 px-3 py-2"
          >
            <Icon className="h-4 w-4 shrink-0 text-muted" aria-hidden />
            <span className="min-w-0">
              <span className="block truncate font-medium text-primary">{item.title}</span>
              {item.subtitle ? (
                <span className="block truncate text-xs text-muted">{item.subtitle}</span>
              ) : null}
            </span>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
